import type { Quill, RangeStatic } from "quill";

type PendingLink = { href: string; index: number };

const pendingByEditor = new WeakMap<Quill, PendingLink>();
const watchedEditors = new WeakSet<Quill>();

const disarm = (editor: Quill) => {
  pendingByEditor.delete(editor);
};

/*
 * Types text at the pending caret as linked text. Quill's own caret format
 * can't do this for a link: it keeps the format in a cursor span inside the
 * `<a>`, and browsers type outside an anchor at its edge. So the keystroke is
 * taken over before the browser inserts it, and Quill inserts it already
 * linked: one change, kept by undo, with no diff of the DOM to interpret.
 */
const watch = (editor: Quill) => {
  if (watchedEditors.has(editor)) return;
  watchedEditors.add(editor);

  editor.root.addEventListener("beforeinput", (event: InputEvent) => {
    const pending = pendingByEditor.get(editor);
    if (!pending) return;

    const range = editor.getSelection();
    if (!range || range.length || range.index !== pending.index) {
      disarm(editor);
      return;
    }

    // Whitespace ends the link and is typed natively, so the browser keeps
    // a trailing space visible with a non-breaking one.
    if (
      event.inputType === "insertText" &&
      event.data &&
      !/^\s/.test(event.data)
    ) {
      event.preventDefault();
      const { cfId: _cfId, ...formats } = editor.getFormat(range.index);
      pending.index += event.data.length;
      editor.insertText(
        range.index,
        event.data,
        { ...formats, link: pending.href },
        "user"
      );
      editor.setSelection(pending.index, 0, "silent");
      return;
    }

    if (event.inputType === "deleteContentBackward" && range.index > 0) {
      event.preventDefault();
      pending.index -= 1;
      editor.deleteText(range.index - 1, 1, "user");
      editor.setSelection(pending.index, 0, "silent");
      return;
    }

    if (event.inputType !== "deleteContentForward") {
      disarm(editor);
    }
  });

  editor.on("selection-change", (range: RangeStatic | null) => {
    const pending = pendingByEditor.get(editor);
    if (!pending || !range) return;
    if (range.length || range.index !== pending.index) {
      disarm(editor);
    }
  });
};

export const armPendingLink = (editor: Quill, href: string, index: number) => {
  watch(editor);
  pendingByEditor.set(editor, { href, index });
};

export const disarmPendingLink = disarm;

export const isPendingLink = (editor: Quill, href: string, index: number) => {
  const pending = pendingByEditor.get(editor);
  return pending?.href === href && pending.index === index;
};
